import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import React from 'react';
import { ApplicationStatus, UIApplication } from 'types';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface Props {
	feedback?: string;
	application?: UIApplication;
}

const RecruiterRejectFeedback = ({ feedback, application }: Props) => {
	if (!feedback && application?.status !== ApplicationStatus.SCREEN_REJECTED) return null;

	// Find the latest rejection date from history for both REJECTED and AUTO_REJECTED statuses
	// Filter all rejection entries and get the most recent one
	const rejectionHistories = application?.history?.filter(
		(h) =>
			h.newStatus === ApplicationStatus.REJECTED ||
			h.newStatus === ApplicationStatus.SCREEN_REJECTED ||
			h.newStatus === ApplicationStatus.AUTO_REJECTED
	) || [];

	// Get the latest rejection entry by sorting by createdAt and taking the most recent
	const latestRejectionHistory = rejectionHistories.length > 0
		? rejectionHistories.reduce((latest, current) => {
			const latestDate = latest?.createdAt ? new Date(latest.createdAt).getTime() : 0;
			const currentDate = current?.createdAt ? new Date(current.createdAt).getTime() : 0;
			return currentDate > latestDate ? current : latest;
		})
		: null;

	const rejectionDate = latestRejectionHistory?.createdAt;
	const isAutoRejected = application?.status === ApplicationStatus.AUTO_REJECTED;
	const isScreenRejected = application?.status === ApplicationStatus.SCREEN_REJECTED;
	const isRejected = application?.status === ApplicationStatus.REJECTED;

	// Convert UTC date from database to user's local timezone
	const formatRejectionDate = (date: Date | string | undefined) => {
		if (!date) return '';
		// Parse the date as UTC (since it's stored in UTC in the database)
		// Then convert to user's local timezone
		return dayjs.utc(date).local().format('DD MMM YYYY, h:mm A');
	};

	return (
		<div className="bg-[#F7F9FB] flex flex-col gap-[27px] mt-4">
			<div className="pb-[10px] border-b border-[#E4E4EB]">
				<div className="text-base font-[600] text-[#1C1C28]">Feedback</div>

				<div className="mt-[8px] text-[#585757] text-sm font-[400]">{feedback}</div>

				{rejectionDate && (isAutoRejected || isScreenRejected || isRejected) && (
					<div className="mt-[8px] text-[#585757] text-xs font-[400] italic">
						{isAutoRejected ? 'Auto-rejected' : isScreenRejected ? 'Screen rejected' : 'Rejected'} on {formatRejectionDate(rejectionDate)}
					</div>
				)}
				{isScreenRejected && !feedback && (
					<div className="mt-[8px] text-[#585757] text-xs font-[400] italic">No feedback was provided for this screen rejection.</div>
				)}
			</div>
		</div>
	);
};

export default RecruiterRejectFeedback;
